import React, { useState, useEffect, ChangeEvent, FormEvent, useMemo } from 'react';
import PageHeader from '../PageHeader';
import { SavablePageProps } from '../../types';

interface Schedule2PageProps extends SavablePageProps {
    onContinue: () => void;
}

// Reusable component for section titles and layout
const Section: React.FC<{ title: string; children: React.ReactNode; className?: string }> = ({ title, children, className = '' }) => (
    <div className={`py-8 border-b border-slate-200 last:border-b-0 print:border-none print:py-4 print:break-inside-avoid ${className}`}>
        <h3 className="text-xl font-semibold text-slate-800 mb-6 print:mb-3 print:text-lg">{title}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-6 print:grid-cols-3 print:gap-y-2 print:gap-x-4">
            {children}
        </div>
    </div>
);

// Reusable component for form input fields
const FormField: React.FC<{
    label: string;
    name: string;
    type?: string;
    placeholder?: string;
    fullWidth?: boolean;
    readOnly?: boolean;
    value?: string | number;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    highlight?: boolean;
    isPrintView?: boolean;
    isStaffInput?: boolean;
}> = ({ label, name, type = 'text', placeholder = '', fullWidth = false, readOnly = false, value, onChange, highlight = false, isPrintView = false, isStaffInput = true }) => {
     if (isPrintView) {
        return (
            <div className={`${fullWidth ? 'md:col-span-2 lg:col-span-3' : ''} print:mb-1`}>
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md break-words print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">
                    {type === 'number' && value ? `RM ${value}` : value || '-'}
                </div>
            </div>
        );
    }
    const isDate = type === 'date';
    const hasValue = value !== undefined && String(value).length > 0;
    const baseBorder = isStaffInput ? 'border-brand-primary border-2 shadow-md' : 'border-slate-300 border shadow-sm hover:border-slate-400';
    const baseClasses = `block w-full px-4 py-2 ${baseBorder} rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary transition-all duration-200`;
    const statefulClasses = readOnly
        ? "bg-slate-100 text-slate-500 cursor-not-allowed border-none hover:border-none"
        : `bg-white placeholder-slate-400 ${
            isDate ? (hasValue ? 'text-slate-900' : 'text-slate-400') : 'text-slate-900'
          }`;
    const inputClasses = `${baseClasses} ${statefulClasses} ${highlight ? 'animate-pulse-highlight ring-2 ring-brand-primary/50' : ''}`;

    return (
        <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''}>
            <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
            <div className="relative">
                <input
                    id={name}
                    name={name}
                    type={type}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    value={value}
                    onChange={onChange}
                    className={inputClasses}
                />
                {isDate && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                        <svg className="h-5 w-5 text-slate-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </div>
                )}
            </div>
        </div>
    );
};

// Reusable component for textarea fields
const TextAreaField: React.FC<{
    label: string;
    name: string;
    placeholder?: string;
    fullWidth?: boolean;
    rows?: number;
    value?: string;
    onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    readOnly?: boolean;
    highlight?: boolean;
    isPrintView?: boolean;
    isStaffInput?: boolean;
}> = ({ label, name, placeholder = '', fullWidth = false, rows = 3, value, onChange, readOnly = false, highlight = false, isPrintView = false, isStaffInput = true }) => {
    if (isPrintView) {
        return (
            <div className={`${fullWidth ? 'md:col-span-2 lg:col-span-3' : ''} print:mb-1`}>
                <label className="block text-sm font-semibold text-slate-700 mb-1 print:mb-0 print:text-xs print:text-slate-500">{label}</label>
                <div className="text-sm text-slate-900 p-2 min-h-[38px] bg-slate-50 rounded-md whitespace-pre-wrap break-words print:p-0 print:min-h-0 print:bg-transparent print:border-b print:border-slate-300 print:rounded-none print:pb-1 print:leading-snug">{value || '-'}</div>
            </div>
        );
    }
    const baseBorder = isStaffInput ? 'border-brand-primary border-2 shadow-md' : 'border-slate-300 border shadow-sm hover:border-slate-400';
    const baseClasses = `block w-full px-4 py-2 ${baseBorder} rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary/80 focus:border-brand-primary transition-all duration-200`;
    const statefulClasses = readOnly
        ? "bg-slate-100 text-slate-500 cursor-not-allowed border-none hover:border-none"
        : "bg-white text-slate-900 placeholder-slate-400";
    
    return (
     <div className={fullWidth ? 'md:col-span-2 lg:col-span-3' : ''}>
        <label htmlFor={name} className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
        <textarea
            id={name}
            name={name}
            rows={rows}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            readOnly={readOnly}
            className={`${baseClasses} ${statefulClasses} ${highlight ? 'animate-pulse-highlight ring-2 ring-brand-primary/50' : ''}`}
        />
    </div>
    );
};

const isFieldEmpty = (value: any): boolean => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string' && value.trim() === '') return true;
    if (typeof value === 'number' && isNaN(value)) return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
};

const Schedule2Page: React.FC<Schedule2PageProps> = ({ data, onUpdate, isSaving, isClientView, isPrintView = false, onContinue }) => {
    const formatDate = (isoString: string | null | undefined) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        if (isNaN(date.getTime())) {
            return '';
        }
        return date.toISOString().split('T')[0];
    };

    const [roomType, setRoomType] = useState(data.room_type || '');
    const [pricePerMonth, setPricePerMonth] = useState(data.price_per_month || '');
    const [deposit, setDeposit] = useState(data.deposit || '');
    const [services, setServices] = useState(data.services_schedule_2 || '');
    const [others, setOthers] = useState(data.others || '');
    const [total, setTotal] = useState(data.total || '');
    const [commencementDate, setCommencementDate] = useState(formatDate(data.commencement_date_schedule_2));
    const [paymentTerms, setPaymentTerms] = useState(data.payment_terms || '');
    const [initialState, setInitialState] = useState(data);

    useEffect(() => {
        setInitialState(data);
        setRoomType(data.room_type || '');
        setPricePerMonth(data.price_per_month || '');
        setDeposit(data.deposit || '');
        setServices(data.services_schedule_2 || '');
        setOthers(data.others || '');
        setTotal(data.total || '');
        setCommencementDate(formatDate(data.commencement_date_schedule_2));
        setPaymentTerms(data.payment_terms || '');
    }, [data]);
    
    const isDirty = useMemo(() => {
        return (
            (initialState.room_type || '') !== roomType ||
            (initialState.price_per_month || '') !== pricePerMonth ||
            (initialState.deposit || '') !== deposit ||
            (initialState.services_schedule_2 || '') !== services ||
            (initialState.others || '') !== others ||
            (initialState.total || '') !== total ||
            formatDate(initialState.commencement_date_schedule_2) !== commencementDate ||
            (initialState.payment_terms || '') !== paymentTerms
        );
    }, [initialState, roomType, pricePerMonth, deposit, services, others, total, commencementDate, paymentTerms]);

    const performSave = async () => {
        const payload = {
            room_type: roomType,
            price_per_month: Number(pricePerMonth),
            deposit: Number(deposit),
            services_schedule_2: services,
            others: String(others),
            total: Number(total),
            commencement_date_schedule_2: commencementDate,
            payment_terms: paymentTerms,
        };
        try {
            await onUpdate(payload);
        } catch (error) {
            console.error("Failed to save Schedule 2:", error);
            throw error;
        }
    };
    
    const handleSaveSubmit = async (e: FormEvent) => {
        e.preventDefault();
        try {
            await performSave();
        } catch (error) {}
    };

    const handleNextClick = async () => {
        try {
            if (isDirty) {
                await performSave();
            }
            onContinue();
        } catch (error) {
            console.log("Save failed, navigation cancelled.");
        }
    };

    const SaveButton = (
        <button type="submit" disabled={isSaving || !isDirty || isClientView} className="w-full sm:w-auto px-8 py-3 bg-slate-600 text-white font-bold rounded-lg shadow-md hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500 transition-all duration-200 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed">
            {isSaving ? 'Saving...' : 'Save'}
        </button>
    );

    const NextButton = (
        <button type="button" onClick={handleNextClick} disabled={isSaving} className="w-full sm:w-auto px-8 py-3 bg-brand-primary text-white font-bold rounded-lg shadow-md hover:bg-brand-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-primary transition-all duration-200 flex items-center justify-center">
            Next
        </button>
    );
    
    const actionButtons = <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">{SaveButton}{NextButton}</div>;

  const commonFieldProps = {
    isPrintView,
    readOnly: isClientView || isPrintView,
  };

  const formContent = (
    <>
      <Section title="Fee Structure">
          <FormField {...commonFieldProps} label="Room Type" name="roomType" placeholder="e.g., Private Deluxe" value={roomType} onChange={e => setRoomType(e.target.value)} highlight={!isClientView && isFieldEmpty(roomType)} />
          <FormField {...commonFieldProps} label="Price Per Month (RM)" name="pricePerMonth" type="number" placeholder="0.00" value={pricePerMonth} onChange={e => setPricePerMonth(Number(e.target.value))} highlight={!isClientView && isFieldEmpty(pricePerMonth)} />
          <FormField {...commonFieldProps} label="Deposit (RM)" name="deposit" type="number" placeholder="0.00" value={deposit} onChange={e => setDeposit(Number(e.target.value))} highlight={!isClientView && isFieldEmpty(deposit)} />
          <TextAreaField {...commonFieldProps} label="Services" name="services" rows={3} placeholder="Describe included services" fullWidth value={services} onChange={e => setServices(e.target.value)} highlight={!isClientView && isFieldEmpty(services)} />
          <FormField {...commonFieldProps} label="Others (RM)" name="others" type="number" placeholder="0.00" value={others} onChange={e => setOthers(e.target.value)} highlight={!isClientView && isFieldEmpty(others)} />
          <FormField {...commonFieldProps} label="Total (RM)" name="total" type="number" placeholder="0.00" value={total} onChange={e => setTotal(Number(e.target.value))} highlight={!isClientView && isFieldEmpty(total)} />
      </Section>
      
      <Section title="Payment Details">
          <FormField {...commonFieldProps} label="Commencement Date" name="commencementDate" type="date" value={commencementDate} onChange={e => setCommencementDate(e.target.value)} highlight={!isClientView && isFieldEmpty(commencementDate)} />
          <TextAreaField {...commonFieldProps} label="Payment Terms" name="paymentTerms" rows={4} placeholder="e.g., Payment due on the 1st of each month via bank transfer." fullWidth value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} highlight={!isClientView && isFieldEmpty(paymentTerms)} />
      </Section>
      
      <div className="py-8 print:py-4 print:break-inside-avoid">
          <h3 className="text-xl font-semibold text-slate-800 mb-4 print:mb-2 print:text-lg">Exclusions</h3>
          <div className="prose prose-sm sm:prose-base prose-slate max-w-none text-slate-600 bg-slate-50 p-5 rounded-lg border border-slate-200 print:p-0 print:border-none print:bg-transparent print:text-xs">
              <p>For the avoidance of doubt, the Service Fee shall only include the aforesaid items and all other costs and expenses including but not limited to the following shall be excluded and shall be charged separately. All such addition costs and expenses incurred shall be become a debt due from the Customer to the Provider:-</p>
              <ul className="list-disc list-inside mt-2 space-y-1 pl-2">
                  <li>disposal diapers; and</li>
                  <li>medications.</li>
              </ul>
          </div>
      </div>
    </>
  );

  return (
    <div className="animate-fade-in">
      <form className="space-y-4" onSubmit={handleSaveSubmit}>
        <PageHeader
            title="Service Agreement - Schedule 2"
            subtitle="Fee Structure and Payment Terms"
            action={actionButtons}
            isPrintView={isPrintView}
        />
        {formContent}

        {!isPrintView && (
            <div className="pt-6 flex justify-end">
                {actionButtons}
            </div>
        )}
      </form>
    </div>
  );
};

export default Schedule2Page;